import { useState, useEffect, useCallback, useRef } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Megaphone, PaperPlaneTilt, MagnifyingGlass, FilePdf, ArrowsClockwise, CheckCircle,
  Prohibit, EnvelopeSimple, Trophy, Play, Eye, FloppyDisk, ArrowCounterClockwise
} from "@phosphor-icons/react";

const STATUS_BADGE = {
  sent: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  applied: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  joined: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

export default function SupplierOutreachPage() {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [log, setLog] = useState([]);
  const [logFilter, setLogFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [templateTab, setTemplateTab] = useState("intro");
  const pollRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, st, lg, status] = await Promise.all([
        API.get("/supplier-outreach/settings"),
        API.get("/supplier-outreach/stats"),
        API.get(`/supplier-outreach/log${logFilter ? `?status=${logFilter}` : ""}`),
        API.get("/supplier-outreach/status"),
      ]);
      setSettings(s.data);
      setStats(st.data);
      setLog(lg.data);
      setRunning(status.data.running);
      setLastRun(status.data.last_run);
    } catch { /* ignore */ }
  }, [logFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (running && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await API.get("/supplier-outreach/status");
          setLastRun(data.last_run);
          if (!data.running) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setRunning(false);
            const lr = data.last_run || {};
            if (lr.status === "stalled") {
              toast.error("Outreach run stalled — please try Run Now again");
            } else if (lr.error) {
              toast.error(`Outreach run hit a problem: ${lr.error}`);
            } else {
              const bits = [`${lr.sent ?? 0} email(s) sent`];
              if (lr.followups_sent) bits.push(`${lr.followups_sent} follow-up(s)`);
              if (lr.failed) bits.push(`${lr.failed} failed`);
              toast.success(`Outreach run finished — ${bits.join(", ")}`);
            }
            loadAll();
          }
        } catch { /* ignore */ }
      }, 3000);
    }
    return () => { if (pollRef.current && !running) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [running, loadAll]);

  const saveSettings = async (patch, msg = "Settings saved") => {
    setSaving(true);
    try {
      const { data } = await API.put("/supplier-outreach/settings", patch);
      setSettings((p) => ({ ...p, ...data }));
      toast.success(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    try {
      await API.post("/supplier-outreach/run", {});
      setRunning(true);
      toast.success("Outreach run started — searching for companies in your gap areas…");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start run");
    }
  };

  const doPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const { data } = await API.post("/supplier-outreach/preview");
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(false); }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await API.post("/supplier-outreach/digest/run-now");
      toast.success(data.sent ? `Outreach digest emailed to ${data.email}` : "Digest generated but email could not be sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send digest");
    } finally { setSendingDigest(false); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const response = await API.get("/supplier-outreach/log/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "supplier-outreach-log.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF export failed");
    } finally { setExporting(false); }
  };

  if (!settings || !stats) return <div className="p-8 text-sm text-muted-foreground" data-testid="outreach-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="supplier-outreach-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight flex items-center gap-2">
            <Megaphone size={28} className="text-primary" /> Supplier Outreach
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Auto-recruits suppliers for your coverage gaps — searches the web, finds contact emails and sends your intro with the application link
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={sendDigest} disabled={sendingDigest} data-testid="outreach-digest-btn">
            <EnvelopeSimple size={16} className="mr-1" /> {sendingDigest ? "Sending…" : "Email Digest"}
          </Button>
          <Button variant="outline" size="sm" onClick={doPreview} disabled={previewing || running} data-testid="outreach-preview-btn">
            <Eye size={16} className="mr-1" /> {previewing ? "Searching…" : "Preview Targets"}
          </Button>
          <Button size="sm" onClick={runNow} disabled={running} data-testid="outreach-run-btn">
            <Play size={16} className="mr-1" /> {running ? "Running…" : "Run Now"}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { label: "Emails Sent", value: stats.total_sent, icon: PaperPlaneTilt, cls: "text-blue-500", tid: "stat-sent" },
          { label: "Today", value: `${stats.sent_today}/${stats.daily_cap}`, icon: EnvelopeSimple, cls: "text-sky-500", tid: "stat-today" },
          { label: "Applied", value: stats.applied, icon: CheckCircle, cls: "text-amber-500", tid: "stat-applied" },
          { label: "Joined", value: stats.joined, icon: Trophy, cls: "text-emerald-500", tid: "stat-joined" },
          { label: "Opt-Outs", value: stats.opted_out, icon: Prohibit, cls: "text-red-500", tid: "stat-optouts" },
          { label: "Gap Combos", value: stats.gap_combos, icon: MagnifyingGlass, cls: "text-violet-500", tid: "stat-gaps" },
        ].map((s) => (
          <div key={s.label} className="border border-border rounded-md p-3 bg-card" data-testid={s.tid}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              <s.icon size={13} className={s.cls} /> {s.label}
            </div>
            <p className="text-xl font-bold font-[Chivo] mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {running && (
        <div className="border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 rounded-md p-3 text-sm flex items-center gap-2" data-testid="outreach-running-banner">
          <ArrowsClockwise size={16} className="animate-spin text-blue-600" />
          Outreach run in progress — searching gap areas and emailing new companies. This can take a minute or two.
        </div>
      )}
      {!running && lastRun?.finished_at && (
        <p className="text-xs text-muted-foreground" data-testid="outreach-last-run">
          Last run: {new Date(lastRun.finished_at).toLocaleString("en-GB")} — {lastRun.sent ?? 0} sent
          {lastRun.followups_sent ? `, ${lastRun.followups_sent} follow-up(s)` : ""}
          {lastRun.targets_found != null ? `, ${lastRun.targets_found} targets found` : ""}{lastRun.error ? ` (error: ${lastRun.error})` : ""}
        </p>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Automation controls */}
        <div className="border border-border rounded-md p-4 bg-card space-y-4">
          <h3 className="text-sm font-semibold font-[Chivo]">Automation</h3>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium">Auto-send daily</p>
              <p className="text-[11px] text-muted-foreground">Runs every morning at your chosen time — searches your gaps and emails new companies up to the daily cap</p>
            </div>
            <div className="flex items-center gap-2">
              <Input type="time" className="w-28 h-8 text-sm" defaultValue={settings.run_time}
                onBlur={(e) => { const v = e.target.value; if (v && v !== settings.run_time) saveSettings({ run_time: v }, `Daily run time set to ${v}`); }}
                data-testid="outreach-runtime-input" />
              <Switch checked={settings.enabled} disabled={saving}
                onCheckedChange={(v) => saveSettings({ enabled: v }, v ? `Auto-send enabled — runs daily at ${settings.run_time} (or press Run Now)` : "Auto-send paused")}
                data-testid="outreach-enabled-switch" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium">Daily email cap</p>
              <p className="text-[11px] text-muted-foreground">Protects your sender reputation — raise gradually as results stay healthy</p>
            </div>
            <div className="flex items-center gap-2">
              <Input type="number" min={1} max={500} className="w-20 h-8 text-sm" defaultValue={settings.daily_cap}
                onBlur={(e) => { const v = parseInt(e.target.value); if (v && v !== settings.daily_cap) saveSettings({ daily_cap: v }, `Daily cap set to ${v}`); }}
                data-testid="outreach-cap-input" />
              <span className="text-xs text-muted-foreground">/day</span>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium">Follow-up nudge</p>
              <p className="text-[11px] text-muted-foreground">One polite follow-up to companies that haven't applied — never chases twice</p>
            </div>
            <div className="flex items-center gap-2">
              <Input type="number" min={1} max={60} className="w-16 h-8 text-sm" defaultValue={settings.followup_days}
                onBlur={(e) => { const v = parseInt(e.target.value); if (v && v !== settings.followup_days) saveSettings({ followup_days: v }, `Follow-up delay set to ${v} days`); }}
                data-testid="outreach-followup-days-input" />
              <span className="text-xs text-muted-foreground">days after</span>
              <Switch checked={settings.followup_enabled} disabled={saving}
                onCheckedChange={(v) => saveSettings({ followup_enabled: v }, v ? "Follow-up nudge enabled" : "Follow-up nudge disabled")}
                data-testid="outreach-followup-switch" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Search query template</Label>
            <Input defaultValue={settings.query_template}
              onBlur={(e) => { if (e.target.value && e.target.value !== settings.query_template) saveSettings({ query_template: e.target.value }, "Query template saved"); }}
              data-testid="outreach-query-input" />
            <p className="text-[10px] text-muted-foreground">Placeholders: {"{service}"} {"{county}"} — e.g. "drainage company in Kent UK"</p>
          </div>
          <div className="pt-2 border-t border-border space-y-2">
            <p className="text-xs font-medium">Google Search keys <span className="text-muted-foreground font-normal">(optional — uses built-in keyless search if empty)</span></p>
            <Input placeholder="Google API key" defaultValue={settings.google_api_key}
              onBlur={(e) => { if (e.target.value !== settings.google_api_key) saveSettings({ google_api_key: e.target.value }, "Google API key saved"); }}
              data-testid="outreach-google-key-input" />
            <Input placeholder="Search Engine ID (cx)" defaultValue={settings.google_cx}
              onBlur={(e) => { if (e.target.value !== settings.google_cx) saveSettings({ google_cx: e.target.value }, "Search Engine ID saved"); }}
              data-testid="outreach-google-cx-input" />
          </div>
          <div className="text-[11px] text-muted-foreground bg-muted/50 rounded p-2.5 leading-relaxed">
            Safety rails: never emails the same address or company twice, skips existing suppliers &amp; applicants,
            skips directories/social sites, includes an unsubscribe link, and honours opt-outs permanently.
          </div>
        </div>

        {/* Email templates */}
        <div className="border border-border rounded-md p-4 bg-card space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold font-[Chivo]">Email Templates</h3>
              <div className="flex rounded-md border border-border overflow-hidden">
                {[["intro", "Intro"], ["followup", "Follow-Up"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setTemplateTab(k)}
                    className={`text-[11px] px-2.5 py-1 transition-colors ${templateTab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid={`template-tab-${k}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            <Button variant="ghost" size="sm" className="h-7 text-xs"
              onClick={() => {
                const patch = templateTab === "intro"
                  ? { subject: settings.defaults.subject, body: settings.defaults.body }
                  : { followup_subject: settings.defaults.followup_subject, followup_body: settings.defaults.followup_body };
                saveSettings(patch, "Template reset to default");
                setSettings((p) => ({ ...p, ...patch }));
              }}
              data-testid="outreach-template-reset-btn">
              <ArrowCounterClockwise size={14} className="mr-1" /> Reset
            </Button>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Subject</Label>
            <Input value={templateTab === "intro" ? settings.subject : settings.followup_subject}
              onChange={(e) => setSettings((p) => ({ ...p, [templateTab === "intro" ? "subject" : "followup_subject"]: e.target.value }))}
              data-testid="outreach-subject-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Body</Label>
            <Textarea value={templateTab === "intro" ? settings.body : settings.followup_body}
              onChange={(e) => setSettings((p) => ({ ...p, [templateTab === "intro" ? "body" : "followup_body"]: e.target.value }))}
              className="text-xs h-64 font-mono" data-testid="outreach-body-input" />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-muted-foreground">
              Placeholders: {"{company_name}"} {"{service}"} {"{county}"} {"{apply_link}"}
            </p>
            <Button size="sm"
              onClick={() => saveSettings(templateTab === "intro"
                ? { subject: settings.subject, body: settings.body }
                : { followup_subject: settings.followup_subject, followup_body: settings.followup_body },
                "Email template saved")}
              disabled={saving} data-testid="outreach-template-save-btn">
              <FloppyDisk size={14} className="mr-1" /> Save Template
            </Button>
          </div>
        </div>
      </div>

      {/* Log */}
      <div className="border border-border rounded-md bg-card">
        <div className="flex flex-wrap items-center justify-between gap-2 p-4 pb-2">
          <h3 className="text-sm font-semibold font-[Chivo]">Outreach Log</h3>
          <div className="flex items-center gap-2">
            {["", "sent", "applied", "joined", "opted_out", "failed"].map((f) => (
              <button key={f} onClick={() => setLogFilter(f)}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${logFilter === f ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
                data-testid={`log-filter-${f || "all"}`}>
                {f === "" ? "All" : f === "opted_out" ? "Opted out" : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
            <Button variant="outline" size="sm" onClick={exportPdf} disabled={exporting || log.length === 0} data-testid="outreach-pdf-btn">
              <FilePdf size={15} className="mr-1" /> PDF
            </Button>
          </div>
        </div>
        {log.length === 0 ? (
          <p className="text-sm text-muted-foreground px-4 pb-4" data-testid="outreach-log-empty">
            No outreach emails yet — press <strong>Preview Targets</strong> to see who would be contacted, or <strong>Run Now</strong> to start.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="outreach-log-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-y border-border">
                  <th className="px-4 py-2">Date</th><th className="px-4 py-2">Company</th><th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Gap Targeted</th><th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {log.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 hover:bg-muted/40" data-testid={`log-row-${r.id}`}>
                    <td className="px-4 py-2 whitespace-nowrap font-mono text-[11px]">{(r.sent_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-2">
                      <p className="font-medium">{r.company_name}</p>
                      <a href={r.website} target="_blank" rel="noreferrer" className="text-[10px] text-muted-foreground hover:text-primary">{r.domain}</a>
                    </td>
                    <td className="px-4 py-2">{r.email}</td>
                    <td className="px-4 py-2"><Badge variant="outline" className="text-[10px]">{r.service} • {r.county}</Badge></td>
                    <td className="px-4 py-2">
                      <Badge className={`text-[10px] ${STATUS_BADGE[r.status] || ""}`}>{(r.status || "").charAt(0).toUpperCase() + (r.status || "").slice(1)}</Badge>
                      {r.followup_sent_at && <Badge variant="outline" className="text-[10px] ml-1" data-testid={`log-followup-${r.id}`}>Followed up</Badge>}
                      {r.opted_out && <Badge className="text-[10px] ml-1 bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">Opted out</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="outreach-preview-dialog">
          <DialogHeader><DialogTitle>Preview — who would be contacted next</DialogTitle></DialogHeader>
          {preview?.targets?.length ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">{preview.count} new company contact(s) found for your next gap searches. Nothing has been sent.</p>
              {preview.targets.map((t, i) => (
                <div key={i} className="border border-border rounded-md p-3 flex items-center justify-between gap-3" data-testid={`preview-target-${i}`}>
                  <div>
                    <p className="text-sm font-medium">{t.company_name}</p>
                    <p className="text-xs text-muted-foreground">{t.email} • <a href={t.website} target="_blank" rel="noreferrer" className="hover:text-primary">{t.domain}</a></p>
                  </div>
                  <Badge variant="outline" className="text-[10px] shrink-0">{t.service} • {t.county}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="preview-empty">No new contactable companies found in the next gap searches — the next run will move on to further gaps automatically.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
